import { AlertCircle, TestTube, CheckCircle, XCircle } from 'lucide-react'
import { FC, useCallback, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import LoadingButton from '@/components/loading-button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import SettingsCard from '@/components/settings-card'

import { FormData } from './'
import useTestLookup from './useTestLookup'
import PhoneInput from '@/components/global/phone-input'

export type Customer = {
  id: string
  title: string
  description: string
}

const TestSheet: FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('')
  const form = useFormContext<FormData>()

  const [customers, test, { timer, error }] = useTestLookup()
  const handleTest = useCallback(async () => {
    const isValid = await form.trigger()
    if (!isValid) return

    const data = form.getValues()
    await test(phoneNumber, data)
  }, [phoneNumber, form, test])

  const hasResults = customers !== undefined
  const isSuccessful = hasResults && !error && customers.length > 0
  const isEmpty = hasResults && !error && customers.length === 0

  return (
    <div className="sticky space-y-6 top-8">
      <SettingsCard
        Icon={TestTube}
        title="Test API"
        description="Test your configuration and preview agent results"
        sections={[
          <div key="test-input" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phoneNumber" className="text-sm font-medium">
                Phone Number
              </Label>
              <PhoneInput
                id="phoneNumber"
                value={phoneNumber}
                onChange={setPhoneNumber}
                type="tel"
                placeholder="+1 234 567 8900"
                pattern="\+[0-9]{1,3}[0-9]{4,14}"
                title="Phone number in E.164 format"
              />
            </div>

            <LoadingButton timer={timer} disabled={!phoneNumber} onClick={handleTest} className="w-full">
              Test Lookup
            </LoadingButton>
          </div>,
        ]}
      />

      {(hasResults || timer.isLoading) && (
        <SettingsCard
          title="Agent Preview"
          description="How results appear to agents"
          sections={[
            <div
              key="results"
              className="space-y-3 overflow-y-auto max-h-96"
              data-testid="customer-lookup-results-container"
            >
              {hasResults && (
                <div className="flex justify-end">
                  <Badge
                    variant={isSuccessful ? 'default' : error ? 'destructive' : 'secondary'}
                    className="flex items-center gap-1"
                  >
                    {error ? (
                      <>
                        <XCircle className="w-3 h-3" /> Failed
                      </>
                    ) : isSuccessful ? (
                      <>
                        <CheckCircle className="w-3 h-3" /> {customers.length} found
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-3 h-3" /> No results
                      </>
                    )}
                  </Badge>
                </div>
              )}
              {timer.isLoading ? (
                <>
                  {[...Array(2)].map((_, idx) => (
                    <div key={idx} className="p-3 space-y-2 border rounded-lg">
                      <Skeleton className="w-3/4 h-4" />
                      <Skeleton className="w-1/2 h-3" />
                      <Skeleton className="w-16 h-5 ml-auto" />
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {(customers ?? []).map((customer, index) => (
                    <div
                      key={customer.id || index}
                      className="p-3 space-y-2 transition-colors border rounded-lg hover:bg-muted/50"
                    >
                      <div className="space-y-1">
                        <h4 className="text-sm font-medium leading-tight">{customer.title || 'Untitled Customer'}</h4>
                        <p className="text-xs leading-tight text-muted-foreground">
                          {customer.description || 'No description'}
                        </p>
                      </div>
                      <div className="flex justify-end">
                        {customer.id ? (
                          <Badge variant="outline" className="h-5 text-xs">
                            {customer.id}
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="h-5 text-xs">
                            NO ID
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {error && (
                <Alert variant="destructive" className="mt-4">
                  <XCircle className="w-4 h-4" />
                  <AlertTitle className="text-sm">Request Failed</AlertTitle>
                  <AlertDescription className="mt-1 text-xs">{error}</AlertDescription>
                </Alert>
              )}

              {isEmpty && (
                <Alert className="mt-4">
                  <AlertCircle className="w-4 h-4" />
                  <AlertTitle className="text-sm">No Results</AlertTitle>
                  <AlertDescription className="mt-1 text-xs">
                    API succeeded but found no customers. Check your path configuration.
                  </AlertDescription>
                </Alert>
              )}
            </div>,
          ]}
        />
      )}
    </div>
  )
}

export default TestSheet
